/**
 * M-21 (Daniel 2026-08-04): web push plumbing for the PWA.
 *
 * The whole reason TR is installable now is so a new lead, a missed
 * call, or a client reply can buzz Daniel's phone the way the KW app
 * never reliably did. The pieces:
 *
 *   registerServiceWorker()  called once from main.tsx on boot
 *   isPushSupported()        capability check for the settings card
 *   isStandalone()           "running from the Home Screen icon?"
 *   isIos()                  Safari-on-iPhone detection for guidance copy
 *   enablePush()             permission + subscribe + POST to the API
 *   disablePush()            unsubscribe + DELETE from the API
 *   getPushState()           what the settings card should show right now
 *
 * iOS reality check, because it shapes everything here: Safari only
 * exposes the Push API to a page launched from a Home Screen icon
 * (iOS 16.4+). In a normal Safari tab `PushManager` simply does not
 * exist, so isPushSupported() is false there and the UI has to walk
 * the user through Add to Home Screen first.
 */
import { apiFetch } from '../auth/api';

/**
 * Register the service worker. Safe to call unconditionally from the
 * entry point: it no-ops on browsers without SW support and never
 * throws (a failed registration must not take down app boot).
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch((err) => {
    console.warn('[push] service worker registration failed', err);
  });
}

/** Can THIS browser context deliver a push at all? */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Launched from a Home Screen icon (or otherwise installed)?
 * `navigator.standalone` is the legacy iOS Safari flag; the media query
 * covers everything else. Either one being true is enough.
 */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true
  );
}

/** iPhone/iPad detection, for the "install first" guidance copy only.
 *  Modern iPadOS masquerades as macOS, hence the maxTouchPoints test. */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

export type PushStatus =
  | 'unsupported'      // browser has no Push API in this context
  | 'ios-needs-install' // iPhone Safari tab: install to Home Screen first
  | 'denied'           // permission permanently refused in browser settings
  | 'off'              // supported, no active subscription
  | 'on';              // subscribed on this device

/** What the settings card should show right now, on this device. */
export async function getPushState(): Promise<PushStatus> {
  if (!isPushSupported()) {
    // The distinction that matters on the team's iPhones: "your browser
    // can't" vs "it can, once you add TR to the Home Screen".
    return isIos() && !isStandalone() ? 'ios-needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

/**
 * The VAPID applicationServerKey arrives base64url-encoded and
 * pushManager.subscribe wants raw bytes. Standard conversion.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Turn push ON for this device.
 *
 * MUST be called from a click handler: both iOS and Chrome refuse the
 * permission prompt outside a user gesture, and iOS is silent about it.
 * Throws with a human-readable message on failure so the card can show
 * exactly what went wrong.
 */
export async function enablePush(): Promise<void> {
  if (!isPushSupported()) {
    throw new Error(
      isIos() && !isStandalone()
        ? 'Add TR to your Home Screen first, then enable push from the installed app.'
        : 'This browser does not support push notifications.',
    );
  }

  const { key } = await apiFetch<{ key: string }>('/api/push/vapid-public-key');
  if (!key) throw new Error('Push is not configured on the server yet.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.subscribe({
    // userVisibleOnly is mandatory everywhere that matters; silent push
    // is not a thing browsers allow ordinary sites.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key).buffer as ArrayBuffer,
  });

  await apiFetch('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription.toJSON()),
  });
}

/**
 * Turn push OFF for this device: drop the browser subscription, then
 * tell the API so the row does not linger and eat a doomed send on
 * every future notification.
 */
export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await apiFetch('/api/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    });
  } catch (err) {
    // The browser side is already unsubscribed; the server row will be
    // reaped on its first 410 anyway. Not worth surfacing to the user.
    console.warn('[push] server-side unsubscribe failed', err);
  }
}
